// @flow

import React from "react";
import { localize } from "redux-i18n";
import { SectionComponent, SchemaForm, Button, SwitchButton, View, BoxField } from "@components";
import type { ReportingParametersData } from "@modules/service/reducer/reporting-parameters/types";
import uiSchema from "./ui-schema";

type OwnProps = $Exact<{
  onSubmit: Function,
  reportingParameters: ReportingParametersData,
  toggleScheduledReportsWarning: (value: boolean) => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

type OwnState = $Exact<{
  edit: boolean,
}>;

export class ScheduledReportsColums extends React.Component<Props, OwnState> {
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }
  state = {
    edit: false,
  };

  submitForm = () => {
    this.formRef.current.submit();
  };

  toggleEdit = () => {
    this.setState({
      edit: !this.state.edit,
    });
  };

  cancelEdit = () => {
    this.setState({
      edit: false,
    });
  };

  handleSubmit = (data: Object) => {
    this.props.onSubmit(data);
    this.toggleEdit();
  };

  get schema() {
    return {
      type: "object",
      required: ["autoDisabling", "reportsExpiry"],
      properties: {
        scheduledReportAccess: {
          type: "array",
          title: "User Access",
          items: {
            type: "string",
            oneOf: [
              {
                title: "Client Admin",
                const: "CLIENT_ADMIN",
              },
              {
                title: "Full Access",
                const: "FULL_ACCESS",
              },
              {
                title: "Advanced User",
                const: "ADVANCED_USER",
              },
              {
                title: "Line Manager",
                const: "LINE_MANAGER",
              },
            ],
          },
        },
        autoDisabling: {
          type: "integer",
          title: "Auto Disabling (Occurrences)",
          minimum: 1,
        },
        reportsExpiry: {
          type: "integer",
          title: "Reports Expiry (Days)",
          minimum: 1,
        },
      },
      urn: "urn:jsonschema:ScheduledReportsSettingsSchema",
    };
  }

  get uiSchema() {
    return uiSchema(this.props.t, this.props.reportingParameters.scheduledReportsEnabled);
  }

  get formData() {
    const { autoDisabling, reportsExpiry } = this.props.reportingParameters;
    const scheduledReportAccess = this.props.reportingParameters.scheduledReportAccess || [];
    return {
      scheduledReportAccess,
      autoDisabling,
      reportsExpiry,
    };
  }

  render() {
    return (
      <SectionComponent
        title={<h5>{this.props.t("SCHEDULED_REPORTS")}</h5>}
        rightLink={
          <View direction="row">
            {!this.state.edit ? (
              <Button materialIcon="edit" onClick={this.toggleEdit}>
                {this.props.t("EDIT")}
              </Button>
            ) : (
              <View direction="row">
                <Button color="secondary" onClick={this.cancelEdit}>
                  {this.props.t("CANCEL")}
                </Button>
                <Button
                  materialIcon="replay_circle_filled"
                  className="ml-2"
                  onClick={this.submitForm}
                >
                  {this.props.t("SUBMIT")}
                </Button>
              </View>
            )}
          </View>
        }
      >
        <View gap={3} flex={1}>
          <BoxField
            title={this.props.t("ABILITY_TO_SCHEDULE_REPORTS")}
            description={this.props.t("ABILITY_TO_SCHEDULE_REPORTS_DESCRIPTION")}
            formStyle
          >
            <SwitchButton
              active={this.props.reportingParameters.scheduledReportsEnabled === true}
              onChange={this.props.toggleScheduledReportsWarning}
              readonly={!this.state.edit}
            />
          </BoxField>
          <SchemaForm
            ref={this.formRef}
            schema={this.schema}
            formData={this.formData}
            onSubmit={this.handleSubmit}
            uiSchema={this.uiSchema}
            readonly={!this.state.edit || !this.props.reportingParameters.scheduledReportsEnabled}
            externalSubmit
            boxGrid
          />
        </View>
      </SectionComponent>
    );
  }
}

export default localize()(ScheduledReportsColums);
